import { api } from '../../shared/api/client'

/** Параметры GET-запроса списка карточек кабинета Wildberries */
export interface WbProductsQueryParams {
  /** CSV значений для отбора по списку артикулов: сопоставляется по vendorCode, nmID, imtID, pim_article */
  articles?: string
  /**
   * Режим проверки списка артикулов: '1' | 'true'. Прочие фильтры (кроме
   * продавца) игнорируются, в ответе только `articles_matched`.
   */
  articles_resolve?: string
  [key: string]: any
}

/** Данные ответа списка карточек Wildberries */
export interface WbProductsResponseData {
  db_products?: any[]
  products?: any[]
  total?: number
  totalIsApproximate?: boolean
  nextCursor?: string | null
  /** Только в режиме articles_resolve — подмножество переданных значений, которым нашлась карточка */
  articles_matched?: string[]
  [key: string]: any
}

export type WbNoPhotoVisualsStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'stopped'

export interface WbNoPhotoVisualsCabinet {
  sellerId: number
  cabinet: string
  name: string
  cards: number
  designs: number
}

export interface WbNoPhotoVisualsCategory {
  sellerId: number
  categoryId: number
  prefix: string
  description: string
  cards: number
}

export interface WbNoPhotoVisualsFunnel {
  sellerId: number
  cabinet: string
  versions: number
  rules: number
}

export interface WbNoPhotoVisualsScope {
  cabinets: WbNoPhotoVisualsCabinet[]
  categories: WbNoPhotoVisualsCategory[]
  funnels: WbNoPhotoVisualsFunnel[]
}

/**
 * Заход операции. Своей таблицы у неё нет — состояние читается из фоновой
 * задачи, поэтому идентификатор захода и есть идентификатор задачи.
 */
export interface WbNoPhotoVisualsRun {
  id: string
  sellerIds: number[]
  status: WbNoPhotoVisualsStatus
  /** Проверочный заход: визуалы рисуются и считаются, наружу ничего не уходит. */
  dryRun: boolean
  /** Макетов под выборку на момент запуска. */
  total: number
  /** Макетов пройдено. */
  designs: number
  /** Макетов принято движком в рендер. */
  rendered: number
  /** Карточек, у которых визуал кабинета уже был: рендер им не заказывали. */
  reused: number
  /** Карточек, чьи кадры нашлись в папке кабинета старого именования. */
  adopted: number
  /** Карточек, чьи фотографии приняла площадка. */
  uploaded: number
  /** Карточек, отклонённых площадкой или транспортом. */
  failed: number
  skippedNoVisual: number
  /** Визуал отрисован, но его URL недоступен для скачивания площадкой. */
  skippedNotPublished: number
  error: string | null
  startedAt: string | null
  completedAt: string | null
}

export interface WbNoPhotoVisualsStarted {
  runId: string
  total: number
  dryRun: boolean
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

/** Ответ гейтвея бывает завёрнут как `{ data: { data: ... } }` или `{ data: ... }`. */
const unwrap = (response: unknown): unknown => {
  const outer = asRecord(response)
  const first = outer.data ?? response
  const firstRecord = asRecord(first)
  return firstRecord.data ?? first
}

const numberOrZero = (value: unknown): number => {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

const nullableText = (value: unknown): string | null => {
  if (value == null) return null
  const text = String(value).trim()
  return text || null
}

const sellerIds = (value: unknown): number[] => {
  let source = value
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source)
    } catch {
      source = []
    }
  }
  return (Array.isArray(source) ? source : [])
    .map(Number)
    .filter((sellerId) => Number.isFinite(sellerId) && sellerId > 0)
}

const normalizeWbNoPhotoVisualsRun = (value: unknown): WbNoPhotoVisualsRun | null => {
  const wrapper = asRecord(value)
  const source = asRecord(wrapper.run ?? value)
  const id = String(source.id ?? source.runId ?? source.run_id ?? source.jobId ?? source.job_id ?? '').trim()
  if (!id) return null
  return {
    id,
    sellerIds: sellerIds(source.sellerIds ?? source.seller_ids),
    status: String(source.status ?? 'queued') as WbNoPhotoVisualsStatus,
    dryRun: source.dryRun === true || source.dry_run === true,
    total: numberOrZero(source.total ?? source.totalCount ?? source.total_count),
    designs: numberOrZero(source.designs ?? source.processed ?? source.processed_count),
    rendered: numberOrZero(source.rendered),
    reused: numberOrZero(source.reused),
    adopted: numberOrZero(source.adopted),
    uploaded: numberOrZero(source.uploaded),
    failed: numberOrZero(source.failed ?? source.failed_count),
    skippedNoVisual: numberOrZero(source.skippedNoVisual ?? source.skipped_no_visual),
    skippedNotPublished: numberOrZero(
      source.skippedNotPublished ?? source.skipped_not_published,
    ),
    error: nullableText(source.error ?? source.errorSummary ?? source.error_summary),
    startedAt: nullableText(source.startedAt ?? source.started_at),
    completedAt: nullableText(source.completedAt ?? source.completed_at),
  }
}

const normalizeWbNoPhotoVisualsStarted = (value: unknown): WbNoPhotoVisualsStarted => {
  const wrapper = asRecord(value)
  const source = asRecord(wrapper.run ?? value)
  const runId = String(source.runId ?? source.run_id ?? source.id ?? source.jobId ?? source.job_id ?? '').trim()
  if (!runId) throw new Error('Сервис не вернул идентификатор операции')
  return {
    runId,
    total: numberOrZero(source.total ?? source.totalCount ?? source.total_count),
    dryRun: source.dryRun === true || source.dry_run === true,
  }
}

/**
 * Новые габариты одной карточки WB: стороны упаковки в сантиметрах, вес с
 * упаковкой в килограммах; не заданное поле не меняется. Карточка — по
 * артикулу WB или по артикулу продавца.
 */
export interface WbDimensionChange {
  nmId?: number
  vendorCode?: string
  length?: number
  width?: number
  height?: number
  weightBrutto?: number
}

export const wildberriesAPI = {
  getSellers: () => api.get('/sellers', { params: { marketplace: 'wildberries' } }),
  getSeller: (sellerId: number) => api.get(`/sellers/${sellerId}`),
  /** params.lean=1 — список каталога без характеристик/размеров/габаритов */
  getProducts: (sellerId: number, params?: any, signal?: AbortSignal) =>
    api.get(`/wildberries/sellers/${sellerId}/products`, { params, signal, timeout: 120000 }),
  /**
   * Проверка списка артикулов на сервере: тот же запрос списка карточек с
   * articles_resolve=1 — сервер игнорирует прочие фильтры и возвращает
   * подмножество переданных значений, для которых нашлась карточка.
   */
  resolveArticles: async (sellerId: number, values: string[]): Promise<string[]> => {
    const response = await api.get(`/wildberries/sellers/${sellerId}/products`, {
      params: { articles: values.join(','), articles_resolve: '1', limit: 1 },
      timeout: 60000,
    })
    const data = response.data?.data ?? response.data
    return Array.isArray(data?.articles_matched) ? data.articles_matched : []
  },
  getProductStatusStats: (sellerId: number) => api.get(`/wildberries/sellers/${sellerId}/products/stats`),
  /** Текущие габариты карточек из каталога PIM — для шаблона XLS */
  getDimensionsCurrent: (sellerId: number, nmIds: number[]) =>
    api.post(`/wildberries/sellers/${sellerId}/dimensions/current`, { nmIds }, { timeout: 120000 }),
  /** Массовая правка габаритов: было → станет по каталогу PIM, без обращения к WB */
  previewDimensions: (sellerId: number, items: WbDimensionChange[]) =>
    api.post(`/wildberries/sellers/${sellerId}/dimensions/preview`, { items }, { timeout: 120000 }),
  /** Отправка габаритов фоном: ответ — { jobId }, итог — getDimensionsJob */
  applyDimensions: (sellerId: number, items: WbDimensionChange[]) =>
    api.post(`/wildberries/sellers/${sellerId}/dimensions/apply`, { items }),
  getDimensionsJob: (sellerId: number, jobId: string) =>
    api.get(`/wildberries/sellers/${sellerId}/dimensions/jobs/${encodeURIComponent(jobId)}`),
  /** Карточки кабинета, заблокированные WB: список пополняет отправка правок */
  getBannedCards: (sellerId: number) => api.get(`/wildberries/sellers/${sellerId}/banned-cards`),
  /** Снять отметку блокировки — когда WB её снял */
  clearBannedCard: (sellerId: number, nmId: number) =>
    api.delete(`/wildberries/sellers/${sellerId}/banned-cards/${nmId}`),
  /** Фасетные счётчики под текущие фильтры каталога */
  getCatalogFacets: (sellerId: number, params?: any, signal?: AbortSignal) =>
    api.get(`/wildberries/sellers/${sellerId}/products/facets`, { params, signal, timeout: 120000 }),
  /** Бренды и категории кабинета для выпадающих фильтров */
  getCatalogFilterOptions: (sellerId: number) =>
    api.get(`/wildberries/sellers/${sellerId}/products/filter-options`, { timeout: 60000 }),
  getWbProductByNmId: (sellerId: number, nmId: number) => api.get(`/wildberries/sellers/${sellerId}/products/${nmId}`),
  refreshProductCard: (sellerId: number, nmId: number) =>
    api.post(`/wildberries/sellers/${sellerId}/products/${nmId}/refresh`, {}, { timeout: 120000 }),
  /** Претензии площадки к артикулу — по кнопке: лимит метода 10 запросов в минуту. */
  getProductClaims: (sellerId: number, nmId: number) =>
    api.get(`/wildberries/sellers/${sellerId}/products/${nmId}/claims`, { timeout: 120000 }),
  getLastSyncInfo: (sellerId: number) => api.get(`/wildberries/sellers/${sellerId}/last-sync`),
  createProducts: (sellerId: number, articles: string[], jobId?: string) =>
    api.post(`/wildberries/sellers/${sellerId}/products/create`, { articles, jobId }),
  syncProducts: (sellerId: number, jobId?: string) =>
    api.post(`/wildberries/sellers/${sellerId}/sync/products`, { jobId }, { timeout: 1800000 }),
  /**
   * Быстрая загрузка: цены запрашиваются только для изменившихся карточек,
   * связывание вынесено. На кабинете с 85 тыс. карточек полная выгрузка цен
   * занимала 86 % времени прогона и шла даже при нулевых изменениях.
   */
  syncProductsFast: (sellerId: number, jobId?: string) =>
    api.post(`/wildberries/sellers/${sellerId}/sync/products-fast`, { jobId }, { timeout: 1800000 }),
  /** Связывание с PIM отдельной задачей; passes — сколько проходов подряд. */
  autoLinkProducts: (sellerId: number, passes = 1, jobId?: string) =>
    api.post(`/wildberries/sellers/${sellerId}/sync/auto-link`, { passes, jobId }, { timeout: 1800000 }),
  getOrders: (sellerId: number, params?: any) => api.get(`/wildberries/sellers/${sellerId}/orders`, { params }),
  getAnalytics: (sellerId: number, params?: any) => api.get(`/wildberries/sellers/${sellerId}/analytics`, { params }),
  getStock: (sellerId: number, params?: any) => api.get(`/wildberries/sellers/${sellerId}/stock`, { params }),
  getPrices: (sellerId: number, params?: any) => api.get(`/wildberries/sellers/${sellerId}/prices`, { params }),
  updatePrices: (sellerId: number, prices: any[]) => api.post(`/wildberries/sellers/${sellerId}/prices`, { prices }),
  updateStock: (sellerId: number, stock: any[]) => api.post(`/wildberries/sellers/${sellerId}/stock`, { stock }),
  getWarehouses: (sellerId: number) => api.get(`/wildberries/sellers/${sellerId}/warehouses`),
  getWBCategories: (sellerId: number) => api.get(`/wildberries/sellers/${sellerId}/categories`),
  getCategoryCharacteristics: (sellerId: number, subjectID: number) =>
    api.get(`/wildberries/sellers/${sellerId}/categories/${subjectID}/characteristics`),
  getPimProductsWbLink: (sellerId: number, params?: { linkStatus: 'linked' | 'not_linked'; limit?: number; offset?: number; search?: string; category?: string }) =>
    api.get(`/wildberries/sellers/${sellerId}/pim-products-wb`, { params: params || {}, timeout: 60000 }),
  getWbProductsForLinking: (sellerId: number, params?: { search?: string; limit?: number; offset?: number }) =>
    api.get(`/wildberries/sellers/${sellerId}/wb-products-for-linking`, { params: params || {}, timeout: 60000 }),
  linkPimToWbProduct: (sellerId: number, pimArticle: string, wbNmId: number) =>
    api.post(`/wildberries/sellers/${sellerId}/link-pim-to-wb`, { pimArticle, wbNmId }),
  unlinkPimFromWbProduct: (sellerId: number, wbNmId: number) =>
    api.post(`/wildberries/sellers/${sellerId}/unlink-pim-from-wb`, { wbNmId }),
  getSuggestedMatches: (sellerId: number, params?: { limit?: number; offset?: number; maxWbProducts?: number }) =>
    api.get(`/wildberries/sellers/${sellerId}/suggested-matches`, { params: params || {}, timeout: 120000 }),
  getUnlinkedCount: (sellerId: number) =>
    api.get(`/wildberries/sellers/${sellerId}/unlinked-count`, { timeout: 15000 }),
  confirmMatch: (sellerId: number, wbNmId: number, pimArticle: string) =>
    api.post(`/wildberries/sellers/${sellerId}/confirm-match`, { wbNmId, pimArticle }),
  rejectMatch: (sellerId: number, wbNmId: number, pimArticle: string) =>
    api.post(`/wildberries/sellers/${sellerId}/reject-match`, { wbNmId, pimArticle }),
  resetAllLinks: (sellerId: number) =>
    api.post(`/wildberries/sellers/${sellerId}/reset-all-links`, {}),
  linkMultipleProducts: (sellerId: number, matches: Array<{ pimArticle: string; wbNmId: number }>) =>
    api.post(`/wildberries/sellers/${sellerId}/link-multiple`, { matches }, { timeout: 120000 }),
  getCachedUnlinkedMatches: (sellerId: number) =>
    api.get(`/wildberries/sellers/${sellerId}/cached-unlinked-matches`, { timeout: 60000 }),
  getAllUnlinkedWbProducts: (sellerId: number, params?: { search?: string; limit?: number; offset?: number }) =>
    api.get(`/wildberries/sellers/${sellerId}/all-unlinked-wb-products`, { params: params || {}, timeout: 60000 }),
  applyArticleMappings: (sellerId: number) =>
    api.post(`/wildberries/sellers/${sellerId}/apply-article-mappings`, {}, { timeout: 300000 }),
  smartAutoLink: (sellerId: number) =>
    api.post(`/wildberries/sellers/${sellerId}/smart-auto-link`, {}, { timeout: 300000 }),
  getLinkingStats: (sellerId: number) =>
    api.get(`/wildberries/sellers/${sellerId}/linking-stats`, { timeout: 15000 }),
  bulkLinkFromPairs: (sellerId: number, pairs: Array<{ vendorCode: string; pimArticle: string }>) =>
    api.post(`/wildberries/sellers/${sellerId}/bulk-link-pairs`, { pairs }, { timeout: 120000 }),
  previewRestoreCharacteristics: (sellerId: number, options?: { nmIDs?: number[]; limit?: number; offset?: number; search?: string; statusFilter?: 'all' | 'matched' | 'unmatched' }) =>
    api.post(`/wildberries/sellers/${sellerId}/preview-restore-characteristics`, options || {}, { timeout: 120000 }),
  restoreCharacteristics: (sellerId: number, body?: { nmIDs?: number[]; jobId?: string; designCodeOverrides?: Record<number, number>; selectedFields?: string[] }) =>
    api.post(`/wildberries/sellers/${sellerId}/restore-characteristics`, body || {}, { timeout: 1800000 }),

  /** Серверная выборка всех карточек WB без фото, сгруппированная для мастера. */
  async getWbNoPhotoVisualsScope(): Promise<WbNoPhotoVisualsScope> {
    const raw = asRecord(unwrap(await api.get('/wb/no-photo-visuals/scope', { timeout: 60_000 })))
    return {
      cabinets: (Array.isArray(raw.cabinets) ? raw.cabinets : []).map((value) => {
        const item = asRecord(value)
        return {
          sellerId: numberOrZero(item.sellerId ?? item.seller_id),
          cabinet: String(item.cabinet ?? ''),
          name: String(item.name ?? item.cabinet ?? ''),
          cards: numberOrZero(item.cards),
          designs: numberOrZero(item.designs),
        }
      }).filter((item) => item.sellerId > 0),
      categories: (Array.isArray(raw.categories) ? raw.categories : []).map((value) => {
        const item = asRecord(value)
        return {
          sellerId: numberOrZero(item.sellerId ?? item.seller_id),
          categoryId: numberOrZero(item.categoryId ?? item.category_id),
          prefix: String(item.prefix ?? ''),
          description: String(item.description ?? item.prefix ?? ''),
          cards: numberOrZero(item.cards),
        }
      }).filter((item) => item.sellerId > 0),
      funnels: (Array.isArray(raw.funnels) ? raw.funnels : []).map((value) => {
        const item = asRecord(value)
        return {
          sellerId: numberOrZero(item.sellerId ?? item.seller_id),
          cabinet: String(item.cabinet ?? ''),
          versions: numberOrZero(item.versions),
          rules: numberOrZero(item.rules),
        }
      }).filter((item) => item.sellerId > 0),
    }
  },

  /** `dryRun` — заход без выхода наружу: рисуем и считаем, но ничего не отправляем. */
  async startWbNoPhotoVisuals(
    sellerIds: number[],
    dryRun = false,
  ): Promise<WbNoPhotoVisualsStarted> {
    return normalizeWbNoPhotoVisualsStarted(
      unwrap(await api.post('/wb/no-photo-visuals', { seller_ids: sellerIds, dry_run: dryRun })),
    )
  },

  async getWbNoPhotoVisualsRuns(): Promise<{
    runs: WbNoPhotoVisualsRun[]
    active: WbNoPhotoVisualsRun | null
  }> {
    const raw = asRecord(unwrap(await api.get('/wb/no-photo-visuals')))
    const runs = (Array.isArray(raw.runs) ? raw.runs : [])
      .map(normalizeWbNoPhotoVisualsRun)
      .filter((item): item is WbNoPhotoVisualsRun => item !== null)
    const explicitActive = normalizeWbNoPhotoVisualsRun(raw.active)
    return {
      runs,
      active: explicitActive
        ?? runs.find((run) => run.status === 'queued' || run.status === 'running')
        ?? null,
    }
  },

  async getWbNoPhotoVisualsRun(runId: string): Promise<WbNoPhotoVisualsRun> {
    const run = normalizeWbNoPhotoVisualsRun(
      unwrap(await api.get(`/wb/no-photo-visuals/${encodeURIComponent(runId)}`)),
    )
    if (!run) throw new Error('Сервис не вернул состояние операции')
    return run
  },

  async stopWbNoPhotoVisuals(runId: string): Promise<WbNoPhotoVisualsRun | null> {
    return normalizeWbNoPhotoVisualsRun(
      unwrap(await api.post(`/wb/no-photo-visuals/${encodeURIComponent(runId)}/stop`, {})),
    )
  },
}
